import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import { apiGet } from '../lib/api.js';
import type { ResponseDetailResponse, ResponseDetail } from '../types/index.js';
import { ResponseContent } from '../components/ResponseContent.js';
import { AnalysisPanel } from '../components/AnalysisPanel.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';
import { Icon, Spinner } from '../components/ui/Icon.js';

export function ResponseDetailPage() {
  const { responseId } = useParams<{ responseId: string }>();
  const navigate = useNavigate();
  const [response, setResponse] = useState<ResponseDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!responseId) {
      setError('No response ID provided');
      setLoading(false);
      return;
    }

    let cancelled = false;

    async function fetchDetail() {
      setLoading(true);
      setError(null);
      try {
        const result = await apiGet<ResponseDetailResponse>(`/api/v1/responses/${responseId}`);
        if (!cancelled) {
          setResponse(result.data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load response');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchDetail();
    return () => { cancelled = true; };
  }, [responseId]);

  const handleBack = () => {
    navigate(-1);
  };

  // Loading state
  if (loading) {
    return (
      <div data-testid="response-detail-page" className="hub-page--reading">
        <div className="flex items-center gap-2 mb-6">
          <button onClick={handleBack} className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900">
            <Icon name="bx-left-arrow-alt" />
            Back to responses
          </button>
        </div>
        <div className="flex justify-center py-20">
          <Spinner className="text-violet-600" />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div data-testid="response-detail-page" className="hub-page--reading">
        <div className="flex items-center gap-2 mb-6">
          <button onClick={handleBack} className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900">
            <Icon name="bx-left-arrow-alt" />
            Back to responses
          </button>
        </div>
        <div className="flex flex-col items-center justify-center py-20">
          <Icon name="bx-error-circle" className="text-red-400" />
          <p className="mt-2 text-lg font-medium text-gray-600">Error loading response</p>
          <p className="mt-1 text-sm text-red-600">{error}</p>
        </div>
      </div>
    );
  }

  // Not found state
  if (!response) {
    return (
      <div data-testid="response-detail-page" className="hub-page--reading">
        <div className="flex items-center gap-2 mb-6">
          <button onClick={handleBack} className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900">
            <Icon name="bx-left-arrow-alt" />
            Back to responses
          </button>
        </div>
        <div className="flex flex-col items-center justify-center py-20">
          <Icon name="bx-error-circle" className="text-gray-300" />
          <p className="mt-2 text-lg font-medium text-gray-400">Response not found</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="response-detail-page" className="hub-page--reading">
      <Breadcrumbs
        className="mb-4"
        items={[{ label: 'Responses', to: '/responses' }, { label: 'Response' }]}
      />
      {/* Navigation bar */}
      <div className="flex items-center justify-between mb-6" data-testid="detail-nav">
        <button
          onClick={handleBack}
          className="hub-btn hub-btn--quiet"
          data-testid="back-button"
        >
          <Icon name="bx-left-arrow-alt" />
          Back to responses
        </button>
      </div>

      {/* Split panel layout */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Left panel: Response Content (~60%) */}
        <div className="lg:col-span-3 rounded-lg border border-gray-200 bg-white p-6">
          <ResponseContent response={response} />
        </div>

        {/* Right panel: Analysis (~40%) */}
        <div className="lg:col-span-2 rounded-lg border border-gray-200 bg-white p-5 lg:sticky lg:top-4 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto">
          <AnalysisPanel response={response} />
        </div>
      </div>
    </div>
  );
}
